import React, { useEffect, useRef, useState } from 'react';

import styled from '@emotion/styled';

import { Box, COLORS, FontWeight, Text, UnstyledButton } from '@clutter/clean';

import { CustomLink } from '@shared/custom_link';

import { DropdownArrow } from '@images/svg/dropdown_arrow';

const MenuDropdownToggle = styled(UnstyledButton)`
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  text-align: center;
  position: relative;
  gap: 4px;

  background: ${COLORS.tealBackground};
  min-width: 90px;
  padding: 7px 12px;
  e &:hover {
    color: ${COLORS.tealBrand};

    & span {
      border-color: ${COLORS.tealBrand};
    }
  }
`;

type Item = { label: React.ReactNode; href: string };

const DropdownContainer = ({ items }: { items: Item[] }) => {
  return (
    <Box
      position={['relative', null, 'absolute']}
      top="135%" // ~12px gap
      left="50%"
      style={{ transform: 'translateX(-50%)' }}
      border={`1px solid ${COLORS.grayBorder}`}
      borderRadius="4px"
      borderStyle={['none', null, 'solid']}
      minWidth="230px"
      background={COLORS.cloud}
    >
      {items.map((item) => (
        <Box key={item.href} margin="16px">
          <CustomLink to={item.href}>
            <Text.Body weight={FontWeight.Medium}>{item.label}</Text.Body>
          </CustomLink>
        </Box>
      ))}
    </Box>
  );
};

export const MenuDropdown: React.FC<{
  isFeatured?: boolean;
  items: Item[];
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  children: React.ReactNode;
}> = ({ children, items }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const listener = (e: MouseEvent) => {
      if (!containerRef.current || !(e.target instanceof Element)) return;

      if (
        !containerRef.current.contains(e.target) ||
        e.target.closest('a') !== null
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', listener);

    return () => document.removeEventListener('click', listener);
  }, []);

  return (
    <Box
      ref={containerRef}
      position="relative"
      margin={[null, null, '0 24px 0 0']}
    >
      <MenuDropdownToggle onClick={() => setIsOpen((open) => !open)}>
        {children}{' '}
        <DropdownArrow
          animate
          fillColor={COLORS.tealPrimary}
          direction={isOpen ? 'up' : 'down'}
        />
      </MenuDropdownToggle>
      {isOpen && <DropdownContainer items={items} />}
    </Box>
  );
};
